import { Calendar, Plus, Users } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import Alert from 'react-bootstrap/Alert';
import Col from 'react-bootstrap/Col';
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import { Button } from '../../app/components';
import MainHeader from '../Common/MainHeader/MainHeader';
import { auth } from '../../firebase/config';
import { useConfirmModal } from '../../hooks/useConfirmModal';
import { addReserva, deleteReserva, getReservasByUser, updateReserva } from '../../services/firestoreService';
import { getClientesByUser } from '../../services/userService';
import { Cliente, Reserva, ReservaFormData } from '../../types';
import ConfirmModal from '../Common/ConfirmModal';
import ReservaForm from './ReservaForm';
import ReservasCalendarSidebar from './ReservasCalendarSidebar';
import ReservasClientesList from './ReservasClientesList';
import './ReservasList.css';
import ReservaViewModal from './ReservaViewModal';

const ReservasList: React.FC = () => {
  const [reservas, setReservas] = useState<Reserva[]>([]);
  const [clientes, setClientes] = useState<Cliente[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>('');
  const [showForm, setShowForm] = useState<boolean>(false);
  const [editingReserva, setEditingReserva] = useState<Reserva | null>(null);
  const [showViewModal, setShowViewModal] = useState<boolean>(false);
  const [viewingReserva, setViewingReserva] = useState<Reserva | null>(null);
  const { modalState, hideConfirm, handleConfirm } = useConfirmModal();
  const [viewMode, setViewMode] = useState<'calendario' | 'clientes'>('calendario');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const uid = auth.currentUser?.uid;

      if (!uid) {
        setError('Usuário não autenticado');
        return;
      }

      const [reservasResult, clientesResult] = await Promise.all([
        getReservasByUser(uid),
        getClientesByUser()
      ]);

      if (reservasResult.success && reservasResult.data) {
        setReservas(reservasResult.data);
      }
      if (clientesResult.success && clientesResult.data) {
        setClientes(clientesResult.data);
      }
    } catch (error) {
      setError('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };



  // Versão sem confirmação para ser usada pelo modal
  const handleDeleteDirect = async (id: string) => {
    try {
      const result = await deleteReserva(id);
      if (result.success) {
        setReservas(reservas.filter(r => r.id !== id));
      } else {
        setError(result.error || 'Erro ao excluir reserva');
      }
    } catch (error) {
      setError('Erro ao excluir reserva');
    }
  };

  const handleEdit = (reserva: Reserva) => {
    setEditingReserva(reserva);
    setShowForm(true);
  };

  const handleView = (reserva: Reserva) => {
    setViewingReserva(reserva);
    setShowViewModal(true);
  };

  const handleSave = async (reservaData: ReservaFormData) => {
    try {
      const uid = auth.currentUser?.uid!;
      let result;
      if (editingReserva) {
        result = await updateReserva(editingReserva.id!, { ...reservaData, userId: uid });
        if (result.success) {
          await loadData(); // Recarrega reservas, clientes e produtos
        } else {
          throw new Error(result.error || 'Erro ao salvar reserva');
        }
      } else {
        result = await addReserva({ ...reservaData, userId: uid } as any);
        if (result.success && result.id) {
          // Para novas reservas, retornar o ID da reserva criada
          return { id: result.id };
        } else {
          throw new Error(result.error || 'Erro ao salvar reserva');
        }
      }
    } catch (error: any) {
      throw error;
    }
  };

  const getClienteByReserva = (reserva: Reserva): Cliente | null => {
    return clientes.find(c => c.id === reserva.clienteId) || null;
  };

  if (loading) {
    return (
      <Container fluid className="p-4">
        <div className="text-center">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Carregando...</span>
          </div>
        </div>
      </Container>
    );
  }

  return (
    <div>
      {/* Header com estatísticas */}
      <MainHeader
        title="Gestão de Reservas"
        subtitle={`Total de ${reservas.length} reserva${reservas.length !== 1 ? 's' : ''} cadastrada${reservas.length !== 1 ? 's' : ''}`}
        icon={<Calendar size={32} />}
      >
        <Button
          btnColor="success"
          onClick={() => {
            setEditingReserva(null);
            setShowForm(true);
          }}
          className="modern-btn d-flex align-items-center gap-2"
          style={{ borderRadius: '12px', padding: '12px 24px' }}
        >
          <Plus size={20} />
          Nova Reserva
        </Button>
      </MainHeader>

      {/* Conteúdo principal - Lista ou Calendário */}
      <Row>
        <Col>
          {error && <Alert variant="danger" className="mb-3">{error}</Alert>}

          <div className="d-flex gap-2 mb-3">
            {([
              { key: 'calendario' as const, label: 'Calendário', icon: <Calendar size={18} /> },
              { key: 'clientes' as const, label: 'Clientes', icon: <Users size={18} /> },
            ]).map(btn => (
              <button
                key={btn.key}
                onClick={() => setViewMode(btn.key)}
                className={`reservas-view-btn${viewMode === btn.key ? ' active' : ''}`}
              >
                {btn.icon}
                {btn.label}
              </button>
            ))}
          </div>

          {viewMode === 'calendario' && (
            <ReservasCalendarSidebar
              reservas={reservas}
              clientes={clientes}
              onViewReserva={handleView}
            />
          )}
          {viewMode === 'clientes' && (
            <ReservasClientesList
              reservas={reservas}
              clientes={clientes}
              onView={handleView}
            />
          )}
        </Col>
      </Row>

      <ReservaForm
        show={showForm}
        onHide={() => {
          setShowForm(false);
          setEditingReserva(null);
        }}
        onSave={handleSave}
        reserva={editingReserva}
        onSuccess={loadData}
      />

      <ReservaViewModal
        show={showViewModal}
        onHide={() => {
          setShowViewModal(false);
          setViewingReserva(null);
        }}
        reserva={viewingReserva}
        cliente={viewingReserva ? getClienteByReserva(viewingReserva) : null}
        onEdit={handleEdit}
        onDelete={handleDeleteDirect}
        onStatusChange={loadData}
      />

      <ConfirmModal
        show={modalState.show}
        onHide={hideConfirm}
        onConfirm={handleConfirm}
        title={modalState.title}
        message={modalState.message}
        type={modalState.type}
        confirmText={modalState.confirmText}
        cancelText={modalState.cancelText}
        loading={modalState.loading}
      />
    </div>
  );
};

export default ReservasList;
